import { useQuery } from '@tanstack/react-query';

import type { GetOrderStatsResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get order stats
 * @param servicesProviderId - Required services provider ID
 */
export const getOrderStats = async (servicesProviderId: string): Promise<GetOrderStatsResponse> => {
	const response = await apiClient.get<GetOrderStatsResponse>(
		ordersEndpoints.stats(servicesProviderId),
	);
	return response.data;
};

/**
 * Hook for fetching order stats
 */
export const useGetOrderStats = () => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.stats(servicesProviderId).queryKey,
		queryFn: () => getOrderStats(servicesProviderId),
		enabled: Boolean(servicesProviderId),
		staleTime: 30_000, // 30 seconds - stats can be cached briefly
		...errorBoundaryOptions,
	});
};
